import React, { useEffect, useMemo, useState } from "react";
import { Button, TextInput, Label, Alert, Spinner } from "flowbite-react";
import { HiLockClosed, HiCheck, HiEye, HiEyeOff } from "react-icons/hi";
import { useParams, useLocation } from "react-router-dom";
import { COMPANY, LOGIN, USERS } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";
import {
  maskCPF as maskCPFBase,
  maskCNPJ as maskCNPJBase,
  maskDocument,
  getDocType,
  onlyCnpjChars,
} from "../utils/masks";
import {
  isValidCPF as isValidCPFBase,
  isValidCNPJ as isValidCNPJBase,
} from "../utils/utils";

/* ============================ UI — STEP HEADER & STEPPER ============================ */

function StepHeader({ step }) {
  const map = {
    1: {
      title: "Identificação",
      subtitle: "Informe seu nome e e-mail para começarmos.",
    },
    2: {
      title: "Contato & Documento",
      subtitle: "Telefone e CPF/CNPJ para habilitar seu acesso com segurança.",
    },
    3: {
      title: "Senha & Revisão",
      subtitle: "Crie uma senha forte, confirme e confira seus dados.",
    },
  };
  const { title, subtitle } = map[step] || map[1];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pt-4 sm:px-6 md:pt-6">
      <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl md:text-3xl">
        {title}
      </h1>
      <p className="mt-1 text-xs text-slate-500 sm:text-sm">{subtitle}</p>
    </div>
  );
}

function Stepper({ step, onStepClick }) {
  const items = ["Identificação", "Contato & Documento", "Senha & Revisão"];
  const progressPct = Math.min(100, ((step - 1) / (items.length - 1)) * 100);

  return (
    <div className="sticky top-0 z-20 w-full bg-gray/85 backdrop-blur supports-[backdrop-filter]:bg-gray/60">
      <div className="mx-auto w-full">
        <div className="relative h-1 w-full">
          <div className="absolute inset-0 rounded-full bg-slate-100" />
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-blue-600 transition-[width] duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-3xl px-2 py-3 sm:px-4 md:py-4">
        <div className="relative">
          <div className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-gray-200" />
          <ol className="relative mt-5 flex items-center justify-between gap-1 sm:gap-2">
            {items.map((label, i) => {
              const idx = i + 1;
              const active = step === idx;
              const done = step > idx;
              const canGo =
                typeof onStepClick === "function" && (done || idx < step);

              return (
                <li
                  key={label}
                  className="flex flex-1 items-center justify-center"
                >
                  <button
                    type="button"
                    disabled={!canGo}
                    onClick={() => canGo && onStepClick(idx)}
                    className={[
                      "group flex items-center gap-2 sm:gap-3 bg-gray-50 px-1",
                      canGo ? "cursor-pointer" : "cursor-default",
                    ].join(" ")}
                    aria-current={active ? "step" : undefined}
                  >
                    <span
                      className={[
                        "flex h-7 w-7 md:h-9 md:w-9 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors sm:h-10 sm:w-10 sm:text-xs",
                        done
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : active
                          ? "border-blue-800 text-blue-900 bg-blue-50"
                          : "border-blue-300 text-blue-400",
                      ].join(" ")}
                    >
                      {done ? <HiCheck className="h-4 w-4" /> : idx}
                    </span>
                    <span
                      className={[
                        "hidden text-xs sm:block",
                        active ? "text-slate-900" : "text-slate-500",
                      ].join(" ")}
                    >
                      {label}
                    </span>
                    <span
                      className={[
                        "block text-[10px] sm:hidden",
                        active ? "text-slate-900" : "text-slate-500",
                      ].join(" ")}
                    >
                      {idx === 1 ? "ID" : idx === 2 ? "Contato" : "Senha"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}

/* ============================ PASSWORD HELPERS ============================ */

function PasswordChecklist({ password }) {
  const v = String(password || "");
  const checks = [
    { label: "≥ 6 caracteres", ok: v.length >= 6 },
    { label: "Maiúscula", ok: /[A-Z]/.test(v) },
    { label: "Número", ok: /[0-9]/.test(v) },
    { label: "Especial", ok: /[^A-Za-z0-9]/.test(v) },
  ];
  return (
    <ul className="mt-2 grid grid-cols-2 gap-y-1 gap-x-3 text-[11px] sm:text-xs">
      {checks.map((c) => (
        <li key={c.label} className="flex items-center gap-2">
          <span
            className={
              "inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold " +
              (c.ok ? "bg-green-500 text-white" : "bg-green-200 text-slate-500")
            }
          >
            {c.ok ? "✓" : "✕"}
          </span>
          <span className={c.ok ? "text-emerald-700" : "text-slate-500"}>
            {c.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PasswordMeter({ score }) {
  const pct = Math.min(100, (score / 4) * 100);
  const classes =
    score >= 3 ? "bg-green-600" : score >= 2 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
      <div className={classes} style={{ width: `${pct}%`, height: "100%" }} />
    </div>
  );
}

/* ============================ SUCCESS PANEL ============================ */

function SuccessPanel({ companyName, onGoLogin }) {
  return (
    <div className="mx-auto w-full max-w-md">
      <div className="rounded-2xl border border-emerald-200 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
          <HiCheck className="h-6 w-6 text-emerald-700" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900">
          Conta criada com sucesso!
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          {companyName ? (
            <>
              Seu acesso para <span className="font-medium">{companyName}</span>{" "}
              foi registrado.
            </>
          ) : (
            <>Seu cadastro foi registrado.</>
          )}{" "}
          Agora você já pode entrar no sistema com seu e-mail e senha.
        </p>
        <div className="mt-6">
          <Button
            onClick={onGoLogin}
            className="bg-blue-700 hover:bg-blue-800 w-full"
          >
            Ir para a tela de login
          </Button>
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          Dica: caso não lembre a senha, use “Esqueci minha senha”.
        </div>
      </div>
    </div>
  );
}

/* ============================ HELPERS — URL/UUID ============================ */

function extractUuidFromPathname(pathname = "") {
  const parts = String(pathname).split("/").filter(Boolean);
  const i = parts.indexOf("register");
  if (i >= 0 && parts[i + 1]) return parts[i + 1];
  return null;
}

/* ============================ PAGE ============================ */

export default function RegisterUserEnterprise() {
  const { uuid: routeUuid } = useParams();
  const location = useLocation();

  const initialUuid = routeUuid || extractUuidFromPathname(location.pathname);

  const [companyUuid, setCompanyUuid] = useState(initialUuid);
  const [companyName, setCompanyName] = useState("");
  const [companyLoading, setCompanyLoading] = useState(!!initialUuid);
  const [hasOnlineRegister, setHasOnlineRegister] = useState(false);
  const [companyError, setCompanyError] = useState("");

  // Buscar nome da empresa por UUID
  useEffect(() => {
    let abort = false;
    async function fetchCompany() {
      if (!companyUuid) {
        setCompanyLoading(false);
        return;
      }
      setCompanyLoading(true);
      setCompanyError("");

      try {
        const url = `${COMPANY.GET_COMPANY_NAME_BY_UUID}/${companyUuid}`;
        const { data } = await axiosInstance.get(url);
        const name = data?.data?.name || data?.data?.socialReason || "";
        if (!abort) {
          if (name) {
            setCompanyName(name);
            setHasOnlineRegister(data?.data?.representative?.hasOnlineRegister);
          } else {
            setCompanyError("Empresa não encontrada.");
            setCompanyName("");
          }
        }
      } catch (e) {
        if (!abort) {
          setCompanyError("Empresa não encontrada.");
          setCompanyName("");
        }
      } finally {
        if (!abort) setCompanyLoading(false);
      }
    }
    fetchCompany();
    return () => {
      abort = true;
    };
  }, [companyUuid]);

  // Bloqueio se o UUID existe e não encontramos empresa/registro online
  const blockAccess =
    !!companyUuid &&
    !companyLoading &&
    (!!companyError || !companyName || !hasOnlineRegister);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({});

  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    cpf: "",
    password: "",
    confirmPassword: "",
  });

  // ===== Duplicidade: somente e-mail =====
  const [dup, setDup] = useState({ email: false });
  const setDupField = (key, val) =>
    setDup((p) => ({ ...p, [key]: Boolean(val) }));

  // Derivados
  const maskedCPF = useMemo(() => maskDocumento(form.cpf), [form.cpf]);
  const maskedPhone = useMemo(
    () => maskPhone(form.phoneNumber),
    [form.phoneNumber]
  );
  const emailValid = useMemo(
    () => !form.email || /\S+@\S+\.\S+/.test(form.email),
    [form.email]
  );
  const docValid = useMemo(
    () => !form.cpf || isValidDocumento(form.cpf),
    [form.cpf]
  );
  const phoneValid = useMemo(
    () => isValidPhone(form.phoneNumber),
    [form.phoneNumber]
  );
  const passwordScore = useMemo(
    () => getPasswordScore(form.password),
    [form.password]
  );
  const passwordsMatch = useMemo(
    () =>
      (form.password?.length ?? 0) > 0 &&
      (form.confirmPassword?.length ?? 0) > 0 &&
      form.password === form.confirmPassword,
    [form.password, form.confirmPassword]
  );

  // Regras de navegação
  const canNextStep1 =
    form.name.trim() && emailValid && form.email && !dup.email;
  const canNextStep2 = phoneValid && isValidDocumento(form.cpf);
  const canSubmit =
    form.password?.length >= 6 &&
    passwordsMatch &&
    isValidDocumento(form.cpf) &&
    emailValid &&
    !dup.email &&
    phoneValid;

  const onChange = (key) => (e) => {
    const raw = e?.target ? e.target.value : e;
    let v = raw;
    if (key === "cpf") v = onlyCnpjChars(raw); // CPF ou CNPJ alfanumérico
    else if (key === "phoneNumber") v = unmask(raw);
    setForm((p) => ({ ...p, [key]: v }));
  };
  const onBlur = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const prev = () => setStep((s) => Math.max(1, s - 1));
  const next = () => setStep((s) => Math.min(3, s + 1));
  const onStepClick = (idx) => {
    if (idx < step) setStep(idx);
  };

  // ======= DEDUPE: apenas e-mail =======
  const checkDuplicateEmail = async (email) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setDupField("email", false);
      return;
    }
    try {
      // Se o endpoint retornar 200, significa que achou usuário => duplicado
      await axiosInstance.get(`${USERS.GET_USER_BY_EMAIL}/${email}`);
      setDupField("email", false);
    } catch {
      // Não achou (404/erro) => não duplicado
      setDupField("email", true);
    }
  };

  // Handlers de blur
  const handleBlurEmail = () => {
    setTouched((t) => ({ ...t, email: true }));
    checkDuplicateEmail(form.email);
  };
  const handleBlurPhone = () =>
    setTouched((t) => ({ ...t, phoneNumber: true }));
  const handleBlurDoc = () => setTouched((t) => ({ ...t, cpf: true }));
  const handleBlurPassword = () =>
    setTouched((t) => ({ ...t, password: true }));
  const handleBlurConfirm = () =>
    setTouched((t) => ({ ...t, confirmPassword: true }));

  const doSubmit = async () => {
    setSuccess(null);
    setError(null);

    if (!canSubmit) {
      setError("Revise os campos antes de prosseguir.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        company: { name: inferCompanyFromUser(form), ...form },
        uuid: companyUuid || null,
      };

      const data = await axiosInstance.post(LOGIN.CREATE_NEW_USER, payload);

      // Mostra o painel de sucesso
      const newId = data?.data?.newUser?.id || data?.newUser?.id;
      const msg =
        newId != null
          ? `Usuário #${newId} criado com sucesso.`
          : "Usuário criado com sucesso.";
      setSuccess(msg);

      // Limpa formulário e volta etapa
      setForm({
        name: "",
        cpf: "",
        phoneNumber: "",
        email: "",
        password: "",
        confirmPassword: "",
      });
      setTouched({});
      setDup({ email: false });
      setStep(1);
    } catch (e) {
      setError("Erro ao cadastrar.");
    } finally {
      setLoading(false);
    }
  };

  /* ============================ GUARD PAGE — INDISPONÍVEL ============================ */
  if (blockAccess) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <header className="flex h-12 items-center bg-white justify-between border-b border-slate-200 px-3 sm:h-14 sm:px-6">
          <div className="text-sm font-medium tracking-tight text-slate-900">
            Cadastro de Acesso
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-2 py-0.5 text-[10px] text-slate-600 sm:px-3 sm:py-1 sm:text-[11px]">
            <HiLockClosed className="h-4 w-4" />
            Site seguro
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
              <HiLockClosed className="h-6 w-6 text-rose-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">
              Esta página não está disponível
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              O código de empresa fornecido é inválido ou não foi encontrado.
              Verifique o link recebido ou contate o responsável.
            </p>
            <div className="mt-6 flex justify-center">
              <Button
                onClick={() => {
                  try {
                    localStorage.removeItem("token");
                  } catch {}
                  window.location.href = "/";
                }}
                className="bg-blue-700 hover:bg-blue-800"
              >
                Ir para a página de login
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* ============================ PÁGINA NORMAL ============================ */
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header fino */}
      <header className="flex h-12 items-center bg-white justify-between border-b border-slate-200 px-3 sm:h-14 sm:px-6">
        <div className="text-sm font-medium tracking-tight text-slate-900">
          Cadastro de Acesso
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-2 py-0.5 text-[10px] text-slate-600 sm:px-3 sm:py-1 sm:text-[11px]">
          <HiLockClosed className="h-4 w-4" />
          Site seguro
        </div>
      </header>

      {/* Aviso da empresa por UUID */}
      <div className="mx-auto w-full max-w-3xl px-4 pt-3 sm:px-6">
        {companyLoading ? (
          <div className="mb-2 inline-flex items-center gap-2 text-xs text-slate-600">
            <Spinner size="sm" /> Buscando empresa...
          </div>
        ) : companyUuid ? (
          <div className="mb-2 text-xs text-slate-600">
            Faça parte do{" "}
            <span className="font-semibold text-slate-900">
              {companyName || "—"}
            </span>
          </div>
        ) : (
          <div className="mb-2 text-xs text-slate-500">
            Nenhum código de empresa na URL. Você pode prosseguir normalmente.
          </div>
        )}
      </div>

      {/* Quando sucesso: painel de sucesso em vez do formulário */}
      {success ? (
        <main className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-12 flex-1">
          <SuccessPanel
            companyName={companyName}
            onGoLogin={() => {
              window.location.href = "/login";
            }}
          />
        </main>
      ) : (
        <>
          {/* Stepper + Header das etapas */}
          <Stepper step={step} onStepClick={onStepClick} />
          <StepHeader step={step} />

          {/* Conteúdo */}
          <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-8 flex-1 pb-24 sm:pb-28">
            {/* STEP 1 */}
            {step === 1 && (
              <section className="space-y-5 sm:space-y-6">
                <div className="grid gap-4 sm:gap-5">
                  <div>
                    <Label htmlFor="name" value="Nome completo" />
                    <TextInput
                      id="name"
                      placeholder="Ex.: Maria Silva"
                      value={form.name}
                      onChange={onChange("name")}
                      onBlur={onBlur("name")}
                      autoComplete="name"
                      autoFocus
                      color={
                        touched.name && !form.name.trim()
                          ? "failure"
                          : undefined
                      }
                      helperText={
                        touched.name && !form.name.trim()
                          ? "Informe seu nome completo."
                          : undefined
                      }
                      className="bg-white"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email" value="E-mail" />
                      {touched.email && (
                        <span
                          className={
                            !dup.email && emailValid
                              ? "text-xs text-emerald-700"
                              : "text-xs text-rose-600"
                          }
                        >
                          {dup.email
                            ? "Já cadastrado"
                            : emailValid
                            ? "Válido"
                            : "Inválido"}
                        </span>
                      )}
                    </div>
                    <TextInput
                      id="email"
                      type="email"
                      placeholder="email@dominio.com"
                      value={form.email}
                      onChange={onChange("email")}
                      onBlur={handleBlurEmail}
                      autoComplete="email"
                      color={
                        (touched.email && !emailValid) || dup.email
                          ? "failure"
                          : undefined
                      }
                      helperText={
                        dup.email
                          ? "Já existe um usuário cadastrado com este e-mail."
                          : touched.email && !emailValid
                          ? "Digite um e-mail válido."
                          : undefined
                      }
                    />
                  </div>
                </div>
              </section>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <section className="space-y-5 sm:space-y-6">
                <div className="grid gap-4 sm:gap-5">
                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="phone" value="Telefone" />
                      {touched.phoneNumber && (
                        <span
                          className={
                            phoneValid
                              ? "text-xs text-emerald-700"
                              : "text-xs text-rose-600"
                          }
                        >
                          {phoneValid ? "Válido" : "Inválido"}
                        </span>
                      )}
                    </div>
                    <TextInput
                      id="phone"
                      placeholder="(11) 90000-0000"
                      value={maskedPhone}
                      inputMode="tel"
                      autoComplete="tel"
                      onChange={onChange("phoneNumber")}
                      onBlur={handleBlurPhone}
                      color={
                        touched.phoneNumber && !phoneValid
                          ? "failure"
                          : undefined
                      }
                      helperText={
                        touched.phoneNumber && !phoneValid
                          ? "Informe um celular válido (10 ou 11 dígitos, máx. 11)."
                          : undefined
                      }
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="cpf" value="Documento (CPF ou CNPJ)" />
                      {touched.cpf && (
                        <span
                          className={
                            !docValid
                              ? "text-xs text-rose-600"
                              : "text-xs text-emerald-700"
                          }
                        >
                          {docValid ? "Válido" : "Inválido"}
                        </span>
                      )}
                    </div>
                    <TextInput
                      id="cpf"
                      value={maskedCPF}
                      inputMode="numeric"
                      onChange={onChange("cpf")}
                      onBlur={handleBlurDoc}
                      color={touched.cpf && !docValid ? "failure" : undefined}
                      helperText={
                        touched.cpf && !docValid
                          ? "Revise os dígitos do documento."
                          : undefined
                      }
                    />
                  </div>
                </div>
              </section>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <section className="space-y-5 sm:space-y-6">
                <div className="grid gap-4 sm:gap-5">
                  <div>
                    <Label htmlFor="password" value="Senha" />
                    <div className="relative">
                      <TextInput
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Mín. 6 caracteres"
                        value={form.password}
                        onChange={onChange("password")}
                        onBlur={handleBlurPassword}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute top-1 inset-y-0 right-2 my-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                        aria-label={
                          showPassword ? "Ocultar senha" : "Mostrar senha"
                        }
                        title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      >
                        {showPassword ? (
                          <HiEyeOff className="h-5 w-5" />
                        ) : (
                          <HiEye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    <div className="mt-2">
                      <PasswordMeter score={passwordScore} />
                    </div>
                    <PasswordChecklist password={form.password} />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="confirmPassword"
                        value="Confirmar senha"
                      />
                      {touched.confirmPassword && (
                        <span
                          className={
                            passwordsMatch
                              ? "text-xs text-emerald-700"
                              : "text-xs text-rose-600"
                          }
                        >
                          {passwordsMatch ? "Coincidem" : "Não coincidem"}
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <TextInput
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Repita a senha"
                        value={form.confirmPassword}
                        onChange={onChange("confirmPassword")}
                        onBlur={handleBlurConfirm}
                        color={
                          touched.confirmPassword && !passwordsMatch
                            ? "failure"
                            : undefined
                        }
                        helperText={
                          touched.confirmPassword && !passwordsMatch
                            ? "As senhas não coincidem."
                            : undefined
                        }
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((s) => !s)}
                        className="absolute top-1 inset-y-0 right-2 my-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                        aria-label={
                          showConfirmPassword
                            ? "Ocultar confirmação"
                            : "Mostrar confirmação"
                        }
                        title={
                          showConfirmPassword
                            ? "Ocultar confirmação"
                            : "Mostrar confirmação"
                        }
                      >
                        {showConfirmPassword ? (
                          <HiEyeOff className="h-5 w-5" />
                        ) : (
                          <HiEye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4 bg-white shadow-sm mt-3">
                    <div className="mb-2 text-sm font-medium text-slate-900">
                      Revisão rápida
                    </div>
                    <dl className="grid grid-cols-1 gap-y-2 text-sm text-slate-600 sm:grid-cols-2">
                      <div>
                        <dt className="text-slate-500">Nome</dt>
                        <dd className="font-medium text-slate-800">
                          {form.name || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">E-mail</dt>
                        <dd className="font-medium text-slate-800">
                          {form.email || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Telefone</dt>
                        <dd className="font-medium text-slate-800">
                          {maskedPhone || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Documento</dt>
                        <dd className="font-medium text-slate-800">
                          {maskedCPF || "—"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {!passwordsMatch && touched.confirmPassword && (
                  <Alert color="warning" className="mt-2">
                    As senhas devem ser idênticas para prosseguir.
                  </Alert>
                )}
              </section>
            )}

            {error && (
              <Alert
                color="failure"
                onDismiss={() => setError(null)}
                className="mt-3"
              >
                Ops! Aconteceu algum erro, por favor tente novamente mais tarde.
              </Alert>
            )}
          </main>
        </>
      )}

      {/* Footer de navegação — escondido quando sucesso */}
      {!success && (
        <footer className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white/90 backdrop-blur">
          <div className="mx-auto flex w-full max-w-3xl flex-col items-stretch gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 pb-[env(safe-area-inset-bottom)]">
            <div className="order-2 text-center text-[10px] text-slate-500 sm:order-1 sm:text-left sm:text-[11px]">
              © {new Date().getFullYear()} • Proteção de dados e boas práticas
              de segurança.
            </div>

            <div className="order-1 grid grid-cols-2 gap-2 sm:order-2 sm:flex sm:items-center sm:gap-2">
              <Button
                color="light"
                onClick={prev}
                disabled={step === 1 || loading}
                className="border-slate-300 text-slate-700"
              >
                Anterior
              </Button>

              {step < 3 ? (
                <Button
                  onClick={next}
                  disabled={
                    loading ||
                    (step === 1 && !canNextStep1) ||
                    (step === 2 && !canNextStep2)
                  }
                  className="bg-blue-700 hover:bg-blue-800"
                >
                  Próximo
                </Button>
              ) : (
                <Button
                  onClick={doSubmit}
                  disabled={loading || !canSubmit}
                  className="bg-blue-700 hover:bg-blue-900"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="mr-2" /> Criando conta...
                    </>
                  ) : (
                    "Criar conta"
                  )}
                </Button>
              )}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

/* ============================ UTILS ============================ */

function unmask(v) {
  return String(v || "").replace(/\D+/g, "");
}
// Documento de cadastro: CPF (só dígitos) ou CNPJ alfanumérico.
const maskDocumento = (v) => maskDocument(v);
const maskCPF = (v) => maskCPFBase(v);
const maskCNPJ = (v) => maskCNPJBase(v);
function maskPhone(v) {
  const s = unmask(v).slice(0, 11); // não deixa passar de 11
  if (!s) return "";
  if (s.length <= 10)
    return s.replace(
      /(\d{2})(\d{4})(\d{0,4})/,
      (m, a, b, c) => `(${a}) ${b}-${c || ""}`
    );
  return s.replace(
    /(\d{2})(\d{5})(\d{0,4})/,
    (m, a, b, c) => `(${a}) ${b}-${c || ""}`
  );
}
function isValidPhone(v) {
  const s = unmask(v);
  // aceita fixo (10) ou celular (11) e nunca mais que 11
  return s.length === 10 || s.length === 11;
}
function getPasswordScore(pw) {
  const value = String(pw || "");
  let score = 0;
  if (value.length >= 6) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/[0-9]/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;
  return score;
}
function isValidDocumento(doc) {
  return getDocType(doc) === "CPF" ? isValidCPFBase(doc) : isValidCNPJBase(doc);
}
function inferCompanyFromUser(obj) {
  const name = obj?.name || "";
  const email = obj?.email || "";
  const base =
    (name && name.trim()) ||
    (email && String(email).split("@")[0]) ||
    "Nova Empresa";
  const pretty = base
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return pretty.length > 2 ? pretty : "Nova Empresa";
}
